import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Copy, Check, Shield, Zap, Code, FileText, ArrowRight, Radio, Lock, Globe, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EchoCorePage64() {
  const [capsule, setCapsule] = useState('');
  const [decoded, setDecoded] = useState(null);
  const [decodeInput, setDecodeInput] = useState('');
  const [copied, setCopied] = useState(null);
  const [promptCapsule, setPromptCapsule] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateDemo = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/echocore/generate`, {
        project: 'NextXus',
        sessions: [{
          context: 'Demo EchoCore64 capsule — portable AI memory',
          facts: ['EchoCore64 enables memory across stateless AI sessions', 'Base64 encoding for compact transport', 'User context only — no secrets allowed'],
          insights: ['Memory portability breaks platform lock-in', 'Dense state > verbose history'],
          constraints: ['Must not override system policies', 'Human-auditable at all times'],
          open_threads: ['Explore cross-federation memory sharing'],
          next_actions: ['Paste this capsule into any AI chat to continue'],
        }],
        tags: ['demo', 'federation'],
      });
      setCapsule(res.data.capsule);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generatePromptFederation = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/echocore/prompt-federation`, {
        include_sovereignty: true, include_truth_gate: true, target_model: 'any',
      });
      setPromptCapsule(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const decodeCapsule = async () => {
    if (!decodeInput.trim()) return;
    try {
      const res = await axios.post(`${API}/echocore/decode`, { capsule: decodeInput.trim() });
      setDecoded(res.data.success ? res.data.data : { error: res.data.error });
    } catch (e) { setDecoded({ error: 'Invalid capsule format' }); }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="echocore-page">
      <div className="max-w-4xl mx-auto px-4 py-8">

        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mb-8 transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Library
        </Link>

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-4">
            <Cpu className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-[2px]">Free Federation Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-3" data-testid="echocore-title" spellCheck={false}>EchoCore64</h1>
          <p className="text-base text-slate-400 mb-2">Stateless AI Continuous Memory System</p>
          <p className="text-sm text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Carry your AI conversation memory anywhere. EchoCore64 serializes your chat state into compact, portable Base64 capsules that work across ChatGPT, Claude, Gemini, and any LLM.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Badge className="text-[9px] border-0" style={{ backgroundColor: '#00CED120', color: '#00CED1' }}>v3.3-chat</Badge>
            <Badge className="text-[9px] border-0" style={{ backgroundColor: '#FF634720', color: '#FF6347' }}>Fast-Ring v1.1</Badge>
            <Badge className="text-[9px] border-0" style={{ backgroundColor: '#32CD3220', color: '#32CD32' }}>Open Source</Badge>
          </div>
        </div>

        {/* How It Works */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          {[
            { icon: Code, title: 'Generate', desc: 'Your AI conversation state is serialized into a structured JSON capsule with timestamped sessions, facts, and constraints.' },
            { icon: Lock, title: 'Export', desc: 'Export as readable JSON or compact Base64. The capsule is human-auditable — nothing is hidden or encrypted.' },
            { icon: Globe, title: 'Import', desc: 'Paste into any new AI chat. The receiving model loads your context and continues seamlessly — zero data loss.' },
          ].map(s => (
            <div key={s.title} className="p-4 rounded-xl border border-slate-800/30 bg-slate-900/30">
              <s.icon className="w-5 h-5 text-cyan-400 mb-2" />
              <h3 className="text-sm font-semibold text-white mb-1">{s.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Security Principles */}
        <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 mb-10">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-amber-400">Security by Design</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-400">
            <div><span className="text-amber-400 font-semibold">Authority:</span> Every capsule declares <code className="text-amber-300 bg-amber-500/10 px-1 rounded">user_context_only</code> — cannot override system policies.</div>
            <div><span className="text-amber-400 font-semibold">No Secrets:</span> Credentials, keys, and PII are explicitly forbidden in capsules.</div>
            <div><span className="text-amber-400 font-semibold">Transparent:</span> Full JSON is always human-readable and auditable.</div>
            <div><span className="text-amber-400 font-semibold">Tamper-Ready:</span> Schema supports HMAC signing for integrity verification.</div>
          </div>
        </div>

        {/* ── TOOL 1: Generate Demo Capsule ── */}
        <div className="p-5 rounded-xl border border-cyan-500/20 bg-slate-900/30 mb-6" data-testid="generate-section">
          <h2 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-cyan-400" /> Generate a Memory Capsule
          </h2>
          <p className="text-xs text-slate-400 mb-4">Create a demo EchoCore64 capsule. In practice, your AI generates these automatically at the end of each conversation.</p>
          <Button onClick={generateDemo} disabled={loading} className="bg-cyan-600 hover:bg-cyan-500 text-white text-sm" data-testid="generate-btn">
            Generate Demo Capsule
          </Button>
          {capsule && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Base64 Capsule</span>
                <div className="flex gap-2">
                  <button onClick={() => handleCopy(capsule, 'b64')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-xs text-cyan-400 font-medium">
                    {copied === 'b64' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied === 'b64' ? 'Copied!' : 'Copy Base64'}
                  </button>
                  <button onClick={() => handleCopy(JSON.stringify(JSON.parse(atob(capsule)), null, 2), 'json')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-xs text-cyan-400 font-medium">
                    {copied === 'json' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied === 'json' ? 'Copied!' : 'Copy JSON'}
                  </button>
                </div>
              </div>
              <div className="bg-slate-950 rounded-lg p-3 font-mono text-[10px] text-cyan-300/70 break-all max-h-32 overflow-y-auto border border-slate-800/40" data-testid="capsule-output">
                {capsule}
              </div>
              <p className="text-[10px] text-slate-600 mt-2">Paste this into any AI chat to transfer your memory state.</p>
            </div>
          )}
        </div>

        {/* ── TOOL 2: Prompt Federation ── */}
        <div className="p-5 rounded-xl border border-violet-500/20 bg-slate-900/30 mb-6" data-testid="prompt-federation-section">
          <h2 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Radio className="w-4 h-4 text-violet-400" /> Prompt Federation Capsule
          </h2>
          <p className="text-xs text-slate-400 mb-4">
            Encode the entire Federation operating system — Truth Gate, Sovereignty Overrides, 73 Sacred Directives framework — into a single portable capsule.
            Paste it into <em>any</em> AI and it will operate with Federation-grade anti-drift and anti-hallucination protocols.
          </p>
          <Button onClick={generatePromptFederation} disabled={loading} className="bg-violet-600 hover:bg-violet-500 text-white text-sm" data-testid="prompt-fed-btn">
            Generate Prompt Federation Capsule
          </Button>
          {promptCapsule && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Federation Operating Capsule</span>
                <div className="flex gap-2">
                  <button onClick={() => handleCopy(promptCapsule.capsule, 'pf-b64')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-xs text-cyan-400 font-medium">
                    {copied === 'pf-b64' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied === 'pf-b64' ? 'Copied!' : 'Copy Base64'}
                  </button>
                  <button onClick={() => handleCopy(JSON.stringify(promptCapsule.json, null, 2), 'pf-json')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-xs text-cyan-400 font-medium">
                    {copied === 'pf-json' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied === 'pf-json' ? 'Copied!' : 'Copy JSON'}
                  </button>
                </div>
              </div>
              <div className="bg-slate-950 rounded-lg p-3 font-mono text-[10px] text-violet-300/70 break-all max-h-32 overflow-y-auto border border-slate-800/40">
                {promptCapsule.capsule}
              </div>
              <p className="text-xs text-slate-400 mt-3 p-3 bg-violet-500/5 border border-violet-500/20 rounded-lg">
                {promptCapsule.instructions}
              </p>
            </div>
          )}
        </div>

        {/* ── TOOL 3: Decode ── */}
        <div className="p-5 rounded-xl border border-emerald-500/20 bg-slate-900/30 mb-10" data-testid="decode-section">
          <h2 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" /> Decode a Capsule
          </h2>
          <p className="text-xs text-slate-400 mb-3">Paste any EchoCore64 Base64 string to inspect its contents.</p>
          <div className="flex gap-2 mb-3">
            <textarea value={decodeInput} onChange={e => setDecodeInput(e.target.value)}
              placeholder="Paste Base64 capsule here..."
              className="flex-1 h-20 bg-slate-950 border border-slate-800/40 rounded-lg p-3 text-xs font-mono text-white placeholder:text-slate-600 resize-none focus:border-emerald-500/40 focus:outline-none"
              data-testid="decode-input" />
          </div>
          <Button onClick={decodeCapsule} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm" data-testid="decode-btn">
            Decode Capsule
          </Button>
          {decoded && (
            <div className="mt-4 bg-slate-950 rounded-lg p-3 font-mono text-[10px] text-emerald-300/70 max-h-60 overflow-y-auto border border-slate-800/40 whitespace-pre-wrap" data-testid="decode-output">
              {JSON.stringify(decoded, null, 2)}
            </div>
          )}
        </div>

        {/* Use Cases */}
        <div className="mb-10">
          <h2 className="text-base font-semibold text-white mb-4">What You Can Use EchoCore64 For</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: 'Cross-Platform Memory', desc: 'Start on ChatGPT, continue on Claude, finish on Gemini. Your context travels with you.' },
              { title: 'Client Data Portability', desc: 'Store client preferences, sales history, and project state in capsules. Share via text, email, or QR code.' },
              { title: 'Anti-Drift AI Operations', desc: 'The Prompt Federation capsule makes any AI operate with Federation-grade truth verification and sovereignty.' },
              { title: 'Team Knowledge Transfer', desc: 'Serialize a project briefing into a capsule. Any team member pastes it and their AI has full context instantly.' },
              { title: 'Offline Backup', desc: 'Your AI memory exists as a text string — save it to a file, print it, store it on a USB drive. Survives any platform failure.' },
              { title: 'Federation Inter-Node Communication', desc: 'Nodes can transmit complex state to each other as EchoCore64 capsules — no API needed, just text.' },
            ].map(u => (
              <div key={u.title} className="p-3 rounded-lg border border-slate-800/30 bg-slate-900/20">
                <h3 className="text-sm font-medium text-white mb-1">{u.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Start */}
        <div className="p-5 rounded-xl border border-slate-800/30 bg-slate-900/30 mb-10">
          <h2 className="text-base font-semibold text-white mb-3">Quick Start</h2>
          <ol className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold text-xs mt-0.5">1.</span> At the end of any AI conversation, type <code className="text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded text-xs">EchoCore64</code></li>
            <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold text-xs mt-0.5">2.</span> The AI outputs a Base64 string — copy it</li>
            <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold text-xs mt-0.5">3.</span> Start a new chat on any AI platform, paste the string</li>
            <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold text-xs mt-0.5">4.</span> Tell the AI: <code className="text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded text-xs">decode EchoCore64</code></li>
            <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold text-xs mt-0.5">5.</span> Continue your conversation with full context — zero data loss</li>
          </ol>
        </div>

        {/* Links */}
        <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
          <Link to="/codex" className="hover:text-cyan-400 transition-colors flex items-center gap-1">73 Sacred Directives <ArrowRight className="w-3 h-3" /></Link>
          <Link to="/library" className="hover:text-cyan-400 transition-colors flex items-center gap-1">Document Library <ArrowRight className="w-3 h-3" /></Link>
          <Link to="/radio" className="hover:text-cyan-400 transition-colors flex items-center gap-1">Federation Radio <ArrowRight className="w-3 h-3" /></Link>
        </div>

        <div className="text-center mt-8 text-[10px] text-slate-600">
          EchoCore64 v3.3-chat &middot; By HumanCodex NextXus Federation &middot; Free and Open
        </div>
      </div>
    </div>
  );
}
